import React, { useState, useRef, useCallback, useEffect } from 'react';
import ReactQuill from 'react-quill';
import mammoth from 'mammoth';
import { Button, Dialog, DialogTitle, DialogContent, DialogActions, CircularProgress, Alert, Box, Menu, MenuItem } from '@mui/material';
import withReactContent from 'sweetalert2-react-content';
import Swal from 'sweetalert2';
import { exportDocumentAsWord } from './WordExport';
import 'react-quill/dist/quill.snow.css';

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000';
const USE_SERVER_URL_CONVERT = process.env.REACT_APP_USE_SERVER_URL_CONVERT === '1';

const SwalReact = withReactContent(Swal);

// Configuração avançada do Quill
const quillModules = {
  toolbar: {
    container: [
      [{ header: [1, 2, 3, 4, 5, 6, false] }],
      [{ font: [] }],
      [{ size: ['small', false, 'large', 'huge'] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ color: [] }, { background: [] }],
      [{ script: 'sub' }, { script: 'super' }],
      [{ list: 'ordered' }, { list: 'bullet' }],
      [{ indent: '-1' }, { indent: '+1' }],
      [{ direction: 'rtl' }],
      [{ align: [] }],
      ['link', 'image', 'video'],
      ['blockquote', 'code-block'],
      ['clean']
    ]
  },
  clipboard: {
    matchVisual: false
  }
};

const quillFormats = [
  'header', 'font', 'size',
  'bold', 'italic', 'underline', 'strike',
  'color', 'background',
  'script',
  'list', 'bullet', 'indent',
  'direction', 'align',
  'link', 'image', 'video',
  'blockquote', 'code-block'
];

const AdvancedDocumentEditor = ({
  value = '',
  onChange,
  pdfUrl,
  onClose,
  title = 'Editor de Documento Avançado'
}) => {
  const [editorContent, setEditorContent] = useState(value);
  const [isLoading, setIsLoading] = useState(false);
  const [conversionStatus, setConversionStatus] = useState('');
  const [exportMenuAnchor, setExportMenuAnchor] = useState(null);
  const quillRef = useRef(null);

  /**
   * Converte um DOCX (ArrayBuffer) em HTML, mantendo imagens inline
   */
  const docxArrayBufferToHtml = useCallback(async (arrayBuffer) => {
    const result = await mammoth.convertToHtml(
      { arrayBuffer },
      {
        convertImage: mammoth.images.inline(async (elem) => {
          const b64 = await elem.read('base64');
          return { src: `data:${elem.contentType};base64,${b64}` };
        }),
        includeDefaultStyleMap: true
      }
    );
    return result.value;
  }, []);

  /**
   * Envia um PDF (File) para o backend e recebe DOCX; depois converte para HTML
   */
  const convertPdfFileToHtml = useCallback(async (file) => {
    const form = new FormData();
    form.append('file', file);

    const resp = await fetch(`${API_BASE}/pdf/convert`, {
      method: 'POST',
      body: form
    });
    if (!resp.ok) throw new Error('Falha na conversão do PDF (upload)');

    const arrayBuffer = await resp.arrayBuffer();
    return await docxArrayBufferToHtml(arrayBuffer);
  }, [docxArrayBufferToHtml]);

  /**
   * Tenta converter via URL diretamente do navegador (precisa CORS habilitado).
   * Se USE_SERVER_URL_CONVERT=1, usa um endpoint opcional no servidor: /api/pdf/convert-from-url
   */
  const convertPdfUrlToHtml = useCallback(async (url) => {
    if (USE_SERVER_URL_CONVERT) {
      const resp = await fetch(`${API_BASE}/pdf/convert-from-url`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pdfUrl: url })
      });
      if (!resp.ok) throw new Error('Falha na conversão do PDF por URL (servidor)');
      const arrayBuffer = await resp.arrayBuffer();
      return await docxArrayBufferToHtml(arrayBuffer);
    }

    // Fallback: tentar baixar no browser e mandar como File (pode falhar por CORS)
    const fileName = url.split('/').pop() || 'arquivo.pdf';
    const r = await fetch(url);
    if (!r.ok) throw new Error('Não foi possível baixar o PDF (CORS?)');
    const blob = await r.blob();
    const file = new File([blob], fileName, { type: blob.type || 'application/pdf' });
    return await convertPdfFileToHtml(file);
  }, [docxArrayBufferToHtml, convertPdfFileToHtml]);

  // ===== Handlers =====
  const handlePdfConversionFromUrl = useCallback(async () => {
    if (!pdfUrl) {
      SwalReact.fire({ title: 'Erro!', text: 'URL do PDF não fornecida.', icon: 'error' });
      return;
    }

    try {
      setIsLoading(true);
      setConversionStatus('Convertendo PDF (URL) ...');
      const html = await convertPdfUrlToHtml(pdfUrl.startsWith('http') ? pdfUrl : `${API_BASE}${pdfUrl}`);
      setEditorContent(html);
      setConversionStatus('PDF convertido com sucesso!');
      setTimeout(() => setConversionStatus(''), 3000);
    } catch (error) {
      console.error('Erro na conversão por URL:', error);
      const tip = USE_SERVER_URL_CONVERT
        ? ''
        : 'Dica: ative REACT_APP_USE_SERVER_URL_CONVERT=1 e implemente /pdf/convert-from-url no backend para evitar CORS.';
      SwalReact.fire({
        title: 'Erro!',
        text: `${error.message || 'Falha na conversão do PDF por URL.'} ${tip}`,
        icon: 'error'
      });
    } finally {
      setIsLoading(false);
    }
  }, [pdfUrl, convertPdfUrlToHtml]);

  // Converter automaticamente o PDF quando o componente montar (se pdfUrl foi passado e não há valor inicial)
  useEffect(() => {
    if (pdfUrl && !value) {
      handlePdfConversionFromUrl();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pdfUrl, value]);

  const handleSave = () => {
    onChange?.(editorContent);
    SwalReact.fire({
      title: 'Sucesso!',
      text: 'Documento salvo com sucesso!',
      icon: 'success',
      timer: 2000,
      showConfirmButton: false
    }).then(() => {
      onClose?.();
    });
  };

  const handleExportWord = () => {
    try {
      const success = exportDocumentAsWord(editorContent, 'documento_editado.docx', 'html');
      if (success) {
        SwalReact.fire({ title: 'Sucesso!', text: 'Documento exportado como Word com sucesso!', icon: 'success', timer: 2000, showConfirmButton: false });
      } else {
        throw new Error('Falha na exportação');
      }
    } catch (error) {
      console.error('Erro ao exportar Word:', error);
      SwalReact.fire({ title: 'Erro!', text: 'Erro ao exportar documento como Word.', icon: 'error' });
    }
    setExportMenuAnchor(null);
  };

  const handleExportHtml = () => {
    try {
      const blob = new Blob([editorContent], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'documento_editado.html';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      SwalReact.fire({ title: 'Sucesso!', text: 'Documento exportado como HTML com sucesso!', icon: 'success', timer: 2000, showConfirmButton: false });
    } catch (error) {
      console.error('Erro ao exportar HTML:', error);
      SwalReact.fire({ title: 'Erro!', text: 'Erro ao exportar documento como HTML.', icon: 'error' });
    }
    setExportMenuAnchor(null);
  };

  return (
    <Dialog
      open={true}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        style: { minHeight: '80vh', maxHeight: '90vh' }
      }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          {title}
          <Box display="flex" gap={1}>
            {/* Importar PDF */}
            {/* <Button variant="outlined" size="small" component="label" disabled={isLoading}>
              Importar PDF
              <input type="file" hidden accept="application/pdf" onChange={handlePdfFileSelect} />
            </Button>

            {/* Upload Word */}
            {/* <Button variant="outlined" size="small" component="label" disabled={isLoading}>
              Upload Word
              <input
                type="file"
                hidden
                accept=".doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                onChange={handleWordFileInput}
              />
            </Button> */}

            {/* Converter por URL (se prop pdfUrl existir) */}
            {/* {pdfUrl && (
              <Button variant="outlined" size="small" onClick={handlePdfConversionFromUrl} disabled={isLoading}>
                Converter URL
              </Button>
            )} */}

            <Button variant="outlined" size="small" onClick={(e) => setExportMenuAnchor(e.currentTarget)} disabled={isLoading}>
              Exportar
            </Button>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent>
        {conversionStatus && (
          <Alert severity="info" sx={{ mb: 2 }}>
            {isLoading && <CircularProgress size={16} sx={{ mr: 1 }} />}
            {conversionStatus}
          </Alert>
        )}

        <Box sx={{ minHeight: '400px', '& .ql-editor': { minHeight: '350px' } }}>
          <ReactQuill
            ref={quillRef}
            theme="snow"
            value={editorContent}
            onChange={setEditorContent}
            modules={quillModules}
            formats={quillFormats}
            placeholder="Cole ou digite o conteúdo do documento aqui..."
            style={{ height: '400px', marginBottom: '50px' }}
          />
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={isLoading}>
          Cancelar
        </Button>
        <Button onClick={handleSave} variant="contained" disabled={isLoading}>
          Salvar Alterações
        </Button>
      </DialogActions>

      {/* Menu de Exportação */}
      <Menu anchorEl={exportMenuAnchor} open={Boolean(exportMenuAnchor)} onClose={() => setExportMenuAnchor(null)}>
        <MenuItem onClick={handleExportWord}>Exportar como Word (.docx)</MenuItem>
        <MenuItem onClick={handleExportHtml}>Exportar como HTML</MenuItem>
      </Menu>
    </Dialog>
  );
};

export default AdvancedDocumentEditor;
